import { useRecentMatters } from '@/features/matters/hooks/useRecentMatters'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ComboBox } from '@progress/kendo-react-dropdowns'
import { PopupPropsContext } from '@progress/kendo-react-popup'
import { HighlightText } from '../../matteroomTab/HighlightText'
import { transformOptions } from '../../matteroomTab/Utils'
import { AddNewPopup } from '../../matteroomTab/AsyncComboBox'

const SEARCH_COMBOBOX_POPUP_WIDTH = 340

// =================== AsyncGroupedComboBox ===================
export const AsyncGroupedComboBox = forwardRef(({
  value,
  onChange,
  fetchOptions,
  config = {},
  editable = true,
  placeholder = 'Select or search',
  localAddedOptions = [],
  onAddNew,
  hasDestination,
  clientNumber = null,
  minWidth = 200,
  height = 40,
  defaultOpen = false,
  matchPopupWidth = false,
}, ref) => {
  const { matters: recentMatters, loading: recentLoading } = useRecentMatters(hasDestination)
  const recentOptions = transformOptions(recentMatters, config)
  const [options, setOptions] = useState([]) // 后端拉取的 options
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [initialLoading, setInitialLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [customSearchText, setCustomSearchText] = useState('')
  const [fetchedValues, setFetchedValues] = useState(new Set())
  const [isInitialized, setIsInitialized] = useState(false)
  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const [popupWidth, setPopupWidth] = useState(SEARCH_COMBOBOX_POPUP_WIDTH)
  const anchorRef = useRef()

  const selected =
    [...localAddedOptions, ...recentOptions, ...options].find(
      o => o.value === value
    ) || null

  const comboRef = useRef(null)
  const fetchOptionsRef = useRef(fetchOptions)

  fetchOptionsRef.current = fetchOptions

  const loading = recentLoading || initialLoading || searchLoading

  useImperativeHandle(ref, () => comboRef.current)

  const normalizeOptionValue = value => {
    if (Array.isArray(value)) return value[0] ?? ''
    return value ?? ''
  }

  const getOptionSearchText = item => {
    const raw = item?.raw || {}
    const configuredFields = config.searchFields || []

    return [
      item?.label,
      item?.value,
      item?.client_name,
      raw.number,
      raw.name,
      raw.client_name,
      ...configuredFields.map(field => raw[field] ?? item?.[field]),
    ]
      .map(value => normalizeOptionValue(value).toString().toLowerCase())
      .filter(Boolean)
      .join(' ')
  }

  const optionMatchesSearch = (item, term) => {
    if (!term) return true
    return getOptionSearchText(item).includes(term)
  }

  const optionMatchesClient = item => {
    if (clientNumber == null || clientNumber === '') return true
    const itemClientNumber = normalizeOptionValue(
      item?.raw?.client_number ?? item?.client_number
    )
    if (itemClientNumber === '' || itemClientNumber == null) return false
    return String(itemClientNumber) === String(clientNumber)
  }

  const uniqueOptionsByValue = items => {
    const uniqueOptions = new Map()
    items.forEach(item => {
      if (item?.value == null || uniqueOptions.has(item.value)) return
      uniqueOptions.set(item.value, item)
    })
    return Array.from(uniqueOptions.values())
  }

  useEffect(() => {
    if (!defaultOpen) return undefined

    setIsOpen(true)
    const frame = window.requestAnimationFrame(() => {
      comboRef.current?.focus()
      setIsOpen(true)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [defaultOpen])

  const updatePopupWidth = () => {
    const comboEl = anchorRef.current?.querySelector?.('.search-combobox')
    const anchorWidth = comboEl?.getBoundingClientRect?.().width
    const width =
      matchPopupWidth && anchorWidth
        ? Math.round(anchorWidth)
        : SEARCH_COMBOBOX_POPUP_WIDTH
    setPopupWidth(width)
    return width
  }

  const syncPopupWidth = () => {
    updatePopupWidth()
  }

  useEffect(() => {
    syncPopupWidth()

    window.addEventListener('resize', syncPopupWidth)
    return () => window.removeEventListener('resize', syncPopupWidth)
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined

    syncPopupWidth()

    const layoutRoot = anchorRef.current?.closest('.k-dialog')
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(syncPopupWidth)

    if (layoutRoot) resizeObserver?.observe(layoutRoot)
    return () => resizeObserver?.disconnect()
  }, [isOpen])

  // 输入防抖
  useEffect(() => {
    if (!editable) return

    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setIsInitialized(true)
    }, 300)

    return () => clearTimeout(handler)
  }, [search, editable])

  // ---------------- 初始化加载 value ----------------
  useEffect(() => {
    if (!editable || !fetchOptionsRef.current) return

    const shouldLoadValueFirst =
      value &&
      !fetchedValues.has(value) &&
      !options.some(opt => opt.value === value)
    const shouldInitLoad = !shouldLoadValueFirst && options.length === 0
    if (!shouldInitLoad && !shouldLoadValueFirst) return
    const query = shouldLoadValueFirst ? value : ''
    const isValueLoad = shouldLoadValueFirst

    let isMounted = true
    ;(async () => {
      setInitialLoading(true)
      try {
        if (!isMounted) return
        const res = await fetchOptionsRef.current(query)
        const raw = Array.isArray(res?.data) ? res.data : res
        const transformed = transformOptions(raw, config)

        if (!isMounted) return

        if (transformed.length) {
          setOptions(prev => [
            ...prev,
            ...transformed.filter(t => !prev.some(p => p.value === t.value)),
          ])
          if (isValueLoad) {
            setFetchedValues(prev => new Set(prev).add(value))
          }
        }
      } finally {
        if (isMounted) setInitialLoading(false)
      }
    })()

    return () => {
      isMounted = false
    }
  }, [value, editable, fetchedValues])

  // ---------------- 搜索加载 ----------------
  useEffect(() => {
    if (!isInitialized || !editable || !fetchOptions || debouncedSearch == null)
      return

    let isMounted = true
    ;(async () => {
      setSearchLoading(true)
      try {
        const res = await fetchOptionsRef.current(debouncedSearch)
        const raw = Array.isArray(res?.data) ? res.data : res
        const transformed = transformOptions(raw, config)

        if (!isMounted) return

        // 搜索结果与父组件临时新增项合并
        const merged = [...transformed]
        if (debouncedSearch?.trim()) {
          const lowerSearch = debouncedSearch.trim().toLowerCase()
          localAddedOptions
            .filter(
              item =>
                optionMatchesClient(item) &&
                optionMatchesSearch(item, lowerSearch)
            )
            .forEach(item => {
              if (!merged.some(o => o.value === item.value)) {
                merged.push(item)
              }
            })
        } else {
          // 没有搜索词时直接合并全部临时新增项（受 client 限制）
          localAddedOptions
            .filter(item => optionMatchesClient(item))
            .forEach(item => {
              if (!merged.some(o => o.value === item.value)) {
                merged.push(item)
              }
            })
        }

        setOptions(merged)
      } finally {
        if (isMounted) setSearchLoading(false)
      }
    })()

    return () => {
      isMounted = false
    }
  }, [debouncedSearch])

  // ---------------- 处理 localAddedOptions ----------------
  const searchTerm = (debouncedSearch ?? '').trim()
  const lowerSearch = searchTerm.toLowerCase()

  // 仅对 localAddedOptions 做过滤（含 client 限制）
  const filteredLocalAdded = localAddedOptions.filter(item => {
    if (!optionMatchesClient(item)) return false
    if (!lowerSearch) return true
    return optionMatchesSearch(item, lowerSearch)
  })

  // ---------------- Add New ----------------

  const shouldShowAdd =
    !loading &&
    ![...options, ...filteredLocalAdded].length &&
    debouncedSearch &&
    !hasDestination
  const mergedOptionsMap = new Map()

  ;[...filteredLocalAdded, ...options].forEach(item => {
    if (!optionMatchesClient(item)) return
    if (!mergedOptionsMap.has(item.value)) {
      mergedOptionsMap.set(item.value, item)
    }
  })

  const enhancedOptions = shouldShowAdd
    ? [
        {
          label: `\u2795 Add "${debouncedSearch}"`,
          value: '__new__',
          isNew: true,
        },
      ]
    : Array.from(mergedOptionsMap.values())
  // ---------------- 处理 Recent：按 client + 搜索过滤 ----------------
  const filteredRecent = uniqueOptionsByValue(
    recentOptions.filter(item => {
      if (!optionMatchesClient(item)) return false
      if (!lowerSearch) return true
      return optionMatchesSearch(item, lowerSearch)
    })
  )

  // ---------------- 处理是否显示分组 ----------------
  let finalOptions
  const hasRecent = filteredRecent.length > 0

  if (hasRecent) {
    // 生成 Recent 的 value 集合，用于去掉重复
    const recentValuesSet = new Set(filteredRecent.map(item => item.value))

    // All 只保留不在 Recent 中的选项
    const allFiltered = enhancedOptions.filter(
      item => !recentValuesSet.has(item.value)
    )

    finalOptions = [{ group: 'Recent Matters', items: filteredRecent }]
    if (allFiltered.length > 0) {
      finalOptions.push({ group: 'All Matters', items: allFiltered })
    }
  } else {
    finalOptions = enhancedOptions // 无分组
  }

  // ---------------- 生成下拉数据 ----------------
  let groupedData
  if (
    Array.isArray(finalOptions) &&
    finalOptions.length > 0 &&
    finalOptions[0]?.group
  ) {
    groupedData = finalOptions.flatMap(group => [
      {
        isGroupLabel: true,
        group: group.group,
        label: '',
        value: `__group__${group.group}`,
      },
      ...group.items.map(it => ({
        ...it,
        group: group.group,
        label: it.label ?? it.value?.toString() ?? '', // 确保普通项也有 label
      })),
    ])
  } else {
    groupedData = finalOptions.map(it => ({
      ...it,
      label: it.label ?? it.value?.toString() ?? '',
    }))
  }

  // ---------------- 下拉渲染 ----------------
  const itemRender = (li, itemProps) => {
    const item = itemProps.dataItem

    if (item?.isGroupLabel) {
      return (
        <div
          style={{
            padding: '0.25rem 1rem',
            fontWeight: 400,
            color: '#525252',
            fontSize: 12,
          }}
        >
          {item.group}
        </div>
      )
    }

    if (item?.isNew) return li

    const raw = item?.raw || {}
    const label = item?.label ? item.label : ''
    const [labelNumberPart, ...labelNameParts] = label.split(' ')
    const numberPart = normalizeOptionValue(
      raw.number ?? item?.number ?? item?.value ?? labelNumberPart
    ).toString()
    const namePart = normalizeOptionValue(
      raw.name ?? item?.name ?? labelNameParts.join(' ')
    ).toString()
    const clientName = normalizeOptionValue(
      raw.client_name ?? item?.client_name
    ).toString()
    const isMatterOption = config.field === 'matter'
    const secondaryText = isMatterOption && clientName ? clientName : ''

    return (
      <HighlightText
        number={numberPart}
        name={namePart}
        secondaryText={secondaryText}
        highlight={search}
        li={li}
        enableTooltip={isMatterOption}
      />
    )
  }

  return (
    <>
      <div ref={anchorRef}>
        <PopupPropsContext.Provider
          value={popupProps => ({
            ...popupProps,
            anchorAlign: {
              horizontal: matchPopupWidth ? 'left' : 'right',
              vertical: 'bottom',
            },
            popupAlign: {
              horizontal: matchPopupWidth ? 'left' : 'right',
              vertical: 'top',
            },
            collision: { horizontal: 'fit', vertical: 'flip' },
          })}
        >
          <ComboBox
            ref={comboRef}
            opened={isOpen}
            onOpen={() => {
              syncPopupWidth()
              setIsOpen(true)
            }}
            onClose={() => setIsOpen(false)}
            onFocus={() => {
              syncPopupWidth()
              setIsOpen(true)
            }}
            onClick={() => {
              syncPopupWidth()
              setIsOpen(true)
            }}
            className="search-combobox"
            style={{ width: '100%', minWidth, height }}
            placeholder={placeholder}
            data={groupedData}
            textField="label"
            dataItemKey="value"
            loading={loading}
            filterable
            suggest
            popupSettings={{
              className: 'custom-combobox-popup',
              popupClass: 'search-combobox-popup search-combobox-popup-panel',
              width: `${popupWidth}px`,
            }}
            onFilterChange={e => setSearch(e.filter?.value ?? '')}
            value={selected}
            onChange={e => {
              const item = e.value
              if (item?.isNew) {
                setCustomSearchText(debouncedSearch)
                setShowAddDialog(true)
                return
              }
              if (item?.isGroupLabel) return
              onChange(item?.value ?? '', item?.raw ?? null)
            }}
            itemRender={itemRender}
          />
        </PopupPropsContext.Provider>
      </div>

      {/* Add New Popup */}
      {anchorRef.current && (
        <AddNewPopup
          show={showAddDialog}
          anchor={anchorRef.current}
          initialValue={customSearchText}
          config={config}
          onClose={() => setShowAddDialog(false)}
          onConfirm={newItem => {
            if (onAddNew) onAddNew(newItem)
            onChange(newItem.value, newItem.raw)
            setShowAddDialog(false)
          }}
        />
      )}
    </>
  )
})
